import { z } from 'zod'
import { metricDefinitionSchema } from './metrics.js'
import {
  roleSchema,
  sourceKindSchema,
  unitSchema,
  userSchema,
} from './primitives.js'

/** Módulo Admin — solo Admin. Usuarios, catálogo de métricas y fuentes. */

export const dataSourceStatusSchema = z.enum(['manual', 'conectada', 'error'])
export type DataSourceStatus = z.infer<typeof dataSourceStatusSchema>

export const DATA_SOURCE_STATUS_LABELS: Record<DataSourceStatus, string> = {
  manual: 'Carga manual',
  conectada: 'Conectada',
  error: 'Con error',
}

/**
 * Estado de una integración. `manual` significa que la fuente todavía no está
 * conectada y los datos entran a mano: mientras haya una sola en `manual`, el
 * banner de datos de demostración sigue justificado.
 */
export const dataSourceSchema = z.object({
  /** La fuente es su propia clave: hay una sola integración por sistema. */
  id: sourceKindSchema,
  nombre: z.string(),
  status: dataSourceStatusSchema,
  /** ISO 8601 del último sync exitoso. null = nunca sincronizó. */
  ultimoSync: z.string().nullable(),
  /** Mensaje del último error. null si no hay error. */
  ultimoError: z.string().nullable(),
  /** Slugs de métricas que alimenta esta fuente. */
  alimenta: z.array(z.string()),
  /** Qué falta para conectarla. Es la instrucción para eng, no copy de relleno. */
  pendiente: z.string().nullable(),
})
export type DataSource = z.infer<typeof dataSourceSchema>

export const updateUserRoleSchema = z.object({
  userId: z.string(),
  rol: roleSchema,
})
export type UpdateUserRoleInput = z.infer<typeof updateUserRoleSchema>

/** Alta o edición de un KPI del catálogo. Agregar un KPI es este POST. */
export const upsertMetricDefinitionSchema = z
  .object({
    slug: z
      .string()
      .min(2)
      .max(60)
      .regex(/^[a-z0-9_]+$/, 'solo minúsculas, números y guion bajo'),
    nombre: z.string().min(2).max(80),
    definicion: z.string().min(10).max(500),
    formula: z.string().max(200).nullable().default(null),
    unidad: unitSchema,
    benchmark: z.string().max(200).nullable().default(null),
    direccion: z.enum(['higher_better', 'lower_better']),
    umbralOk: z.number().nullable().default(null),
    umbralWarning: z.number().nullable().default(null),
    source: sourceKindSchema,
    ownerId: z.string().nullable().default(null),
  })
  .refine((d) => d.umbralOk !== null || d.umbralWarning !== null, {
    message: 'una métrica necesita al menos un umbral para tener semáforo',
    path: ['umbralOk'],
  })
export type UpsertMetricDefinitionInput = z.infer<
  typeof upsertMetricDefinitionSchema
>

export const adminPayloadSchema = z.object({
  usuarios: z.array(userSchema),
  metricas: z.array(metricDefinitionSchema),
  fuentes: z.array(dataSourceSchema),
})
export type AdminPayload = z.infer<typeof adminPayloadSchema>

/**
 * Cómo resuelve identidad el Worker en este deploy. Excluyentes: nunca los dos.
 *
 *  - `access`: Cloudflare Access delante del dominio inyecta
 *    `Cf-Access-Authenticated-User-Email`. Es la vía definitiva.
 *  - `share`: puerta temporal por link firmado por email. El Worker **ignora**
 *    el header de Access, porque sin Access delante ese header lo manda
 *    cualquiera con un `curl`.
 *
 * El front lo necesita para un solo fin: avisar en pantalla que el modo `share`
 * es provisorio. Sin ese aviso, lo provisorio se vuelve permanente por olvido.
 */
export const authModeSchema = z.enum(['access', 'share'])
export type AuthMode = z.infer<typeof authModeSchema>

/**
 * Configuración que el front necesita del servidor y no puede inventar:
 * si los datos son dummy (banner) y las bases de los deep links (por env).
 */
export const appConfigSchema = z.object({
  /** true mientras alguna fuente siga en `manual`: prende el banner. */
  demoData: z.boolean(),
  /**
   * Modo de autenticación del deploy. Lo informa el servidor y no una constante
   * del front: el front no puede saber si Access está delante.
   */
  authMode: authModeSchema,
  /** 'YYYY-MM' del período por defecto del selector. */
  periodoActual: z.string(),
  /** Bases de URL de los sistemas de origen, ya resueltas por entorno. */
  deepLinks: z.object({
    plane: z.object({
      baseUrl: z.string(),
      workspaceSlug: z.string(),
      projectId: z.string(),
    }),
    close: z.object({ baseUrl: z.string() }),
    stripe: z.object({ baseUrl: z.string() }),
    plataforma: z.object({ baseUrl: z.string() }),
    searchConsole: z.object({ baseUrl: z.string(), resourceId: z.string() }),
    /**
     * Quinto sistema de origen. Los dos campos llegan vacíos mientras no se
     * confirme si la instancia es cloud o self-hosted: Admin los muestra vacíos
     * a propósito, que es cómo se ve "falta configurar esto".
     */
    langfuse: z.object({ baseUrl: z.string(), projectId: z.string() }),
  }),
})
export type AppConfig = z.infer<typeof appConfigSchema>
